import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Menu, Item } from 'react-contexify';
import 'react-contexify/dist/ReactContexify.css';
import { getLocalParticipant, getParticipantById, muteRemoteParticipant, PARTICIPANT_ROLE, pinParticipant, SET_MODERATOR_PIN, isScreenShareParticipant, checkModeratorPinRateLimit } from '../../../base/participants';
import { openChat, setIsPollsTabFocused } from '../../../chat/actions.web';
import { closeParticipantsPaneModal } from '../../../participants-pane/actions';
import { pollsTabFocusedHandler } from '../../../conference/actions.web';
import { getCurrentConference, setObjectFit } from '../../../base/conference';
import { setLargeVideoFullscreen } from '../../../large-video/actions.any';
import { openDialog } from '../../../base/dialog';
import { openGrantModeratorDialog } from '../../../video-menu/actions.web';
import { KickRemoteParticipantDialog, MuteEveryoneDialog, MuteEveryonesVideoDialog, MuteRemoteParticipantDialog, MuteRemoteParticipantsVideoDialog, VolumeControlDialog, ForceMuteRemoteDialog, ForceMuteRemoteParticipantVideoDialog, DemoteToVisitorDialog } from '../../../video-menu/components/web';
import { MEDIA_TYPE } from '../../../base/media';
import { close } from '../../../participants-pane/actions';
import { NOTIFICATION_TYPE, showNotification } from '../../../notifications';
import { isParticipantAudioMuted, isParticipantVideoMuted } from '../../../base/tracks';
import { stateType } from '../../../app/stateTypes';
import { clearRaisedHandByParticipant } from '../../../participants-pane/functions';
import { sliceLengthyCharacters } from '../../../base/util';
import { Icon, IconMessage, IconContain, IconCover, IconModeratorPin, IconUserPinned, IconVolume, IconMicDisabled, IconCameraDisabled, IconCrown, IconMuteEveryoneElse, IconMuteVideoEveryoneElse, IconKick, IconRaisedHandSlash, IconUserGroup, IconZoomIn, IconZoomOut } from '../../../base/icons';
import { setLocalZoomShareApproved, setModeratorZoomShareApproved } from '../../../zoom-sync/actions';
import { getLocalZoomShareApproved, getModeratorZoomShareApproved } from '../../../zoom-sync/functions';
import { isDeviceLandScape } from '../../../base/environment/environment';
/**
 * RemoteVideoContextMenu - A React functional component that renders a context menu
 * for remote participant video thumbnails using react-contextify.
 *
 * @param {Object} props - Component props.
 * @param {string} props.participantId - The participant's ID.
 * @param {string} props.videoSpanId - The video container span ID for the context menu selector.
 * @param {boolean} props.showParticipantName - Whether to show participant name at the top (for large video).
 * @returns {JSX.Element} The context menu component.
 */
export function RemoteVideoContextMenu({ participantId, videoSpanId, showParticipantName = false }) {
    var _a, _b;
    const dispatch = useDispatch();
    const { t } = useTranslation();
    // Get all needed state from Redux - ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURN
    const conference = useSelector(getCurrentConference);
    const participant = useSelector((state) => getParticipantById(state, participantId));
    const localParticipant = useSelector((state) => getLocalParticipant(state));
    const { filmStripView, objectFit, lockAudio, lockVideo } = useSelector((state) => state['features/base/conference']);
    const { largevideofullscreen } = useSelector((state) => state['features/large-video']);
    const { remoteVideoMenu = {}, disableRemoteMute } = useSelector((state) => state['features/base/config']);
    const isMainModerator = useSelector((state) => { var _a, _b, _c; return ((_b = (_a = state[stateType.base.jwt]) === null || _a === void 0 ? void 0 : _a.user) === null || _b === void 0 ? void 0 : _b.moderator) || ((_c = state[stateType.base.jwt]) === null || _c === void 0 ? void 0 : _c.moderator); });
    const isVisitorsModeSupported_flag = false; //useSelector(isVisitorModeSupported);
    // Check mute states - must be called before early return
    const isAudioMuted = useSelector((state) => participant ? isParticipantAudioMuted(participant, state) : false);
    const isVideoMuted = useSelector((state) => participant ? isParticipantVideoMuted(participant, state) : false);
    // Check if private chat is disabled
    const isPrivateChatDisabled = useSelector((state) => { var _a; return (_a = state['features/chat']) === null || _a === void 0 ? void 0 : _a.isPrivateChatDisabled; });
    const isModeratorZoomShareApproved = useSelector(getModeratorZoomShareApproved);
    const isLocalZoomShareApproved = useSelector(getLocalZoomShareApproved);
    const isZoomForEveryone = Boolean(isModeratorZoomShareApproved || isLocalZoomShareApproved);
    // Menu ID for this context menu
    const menuId = `context-menu-${videoSpanId}`;
    // Check moderator status
    const _isModerator = Boolean((localParticipant === null || localParticipant === void 0 ? void 0 : localParticipant.role) === PARTICIPANT_ROLE.MODERATOR);
    // Check if participant is a fake participant
    const isFakeParticipant = (_a = participant === null || participant === void 0 ? void 0 : participant.isFakeParticipant) !== null && _a !== void 0 ? _a : false;
    const isScreenShare = isScreenShareParticipant(participant);
    // Check if currently pinned
    const currentlyPinned = (participant === null || participant === void 0 ? void 0 : participant.pinned) || false;
    // Check moderator status
    const isTargetModerator = (participant === null || participant === void 0 ? void 0 : participant.role) === PARTICIPANT_ROLE.MODERATOR;
    // Check disableKick config
    const { disableKick } = remoteVideoMenu;
    // Handler functions - ALL useCallback hooks must be called before any early return
    const handlePrivateMessage = useCallback((e) => {
        var _a, _b;
        (_b = (_a = e === null || e === void 0 ? void 0 : e.event) === null || _a === void 0 ? void 0 : _a.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(_a);
        if (participant) {
            dispatch(openChat(participant));
            dispatch(closeParticipantsPaneModal());
            dispatch(close());
            dispatch(pollsTabFocusedHandler(false));
            dispatch(setIsPollsTabFocused(false));
        }
    }, [dispatch, participant]);
    const handleObjectFit = useCallback((e) => {
        var _a, _b;
        (_b = (_a = e === null || e === void 0 ? void 0 : e.event) === null || _a === void 0 ? void 0 : _a.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(_a);
        const currentObjectFit = objectFit || 'cover';
        dispatch(setObjectFit(currentObjectFit === 'cover' ? 'contain' : 'cover'));
    }, [dispatch, objectFit]);
    const handleToggleZoomShare = useCallback((e) => {
        var _a, _b;
        (_b = (_a = e === null || e === void 0 ? void 0 : e.event) === null || _a === void 0 ? void 0 : _a.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(_a);
        const nextValue = !isZoomForEveryone;
        dispatch(setModeratorZoomShareApproved(nextValue));
        dispatch(setLocalZoomShareApproved(nextValue));
    }, [dispatch, isZoomForEveryone]);
    const handlePin = useCallback((e) => {
        var _a, _b;
        (_b = (_a = e === null || e === void 0 ? void 0 : e.event) === null || _a === void 0 ? void 0 : _a.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(_a);
        // Toggle pin state: if currently pinned, unpin (false), otherwise pin (true)
        dispatch(pinParticipant(participantId, !currentlyPinned));
    }, [dispatch, participantId, currentlyPinned]);
    const handleLargeVideoFullscreen = useCallback((e) => {
        var _a, _b;
        (_b = (_a = e === null || e === void 0 ? void 0 : e.event) === null || _a === void 0 ? void 0 : _a.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(_a);
        dispatch(pinParticipant(participantId, true));
        // Delay layout change to prevent react-contexify from crashing if the menu is unmounted while handling the click
        setTimeout(() => {
            dispatch(setLargeVideoFullscreen(!largevideofullscreen));
        }, 100);
    }, [dispatch, participantId, largevideofullscreen]);
    const participantModeratorPin = useCallback((e) => {
        var _a, _b;
        (_b = (_a = e === null || e === void 0 ? void 0 : e.event) === null || _a === void 0 ? void 0 : _a.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(_a);
        if (!conference || !participant) {
            return;
        }
        if (checkModeratorPinRateLimit(dispatch, t)) {
            return;
        }
        const myId = conference.myUserId();
        const isModeratorPinned = participant && participant.moderatorPin;
        const pinAction = isModeratorPinned ? 'unpin' : 'pin';
        conference.sendMessage({
            type: SET_MODERATOR_PIN,
            participant_id: participantId,
            senderId: myId,
            action: pinAction
        });
        // Show notification
        const notificationKey = pinAction === 'pin'
            ? t('videothumbnail.pinByModerator')
            : t('videothumbnail.unpinByModerator');
        dispatch(showNotification({
            appearance: NOTIFICATION_TYPE.NORMAL,
            titleKey: notificationKey,
            descriptionKey: t('videothumbnail.moderatorPinDiscription')
        }, 3000));
    }, [dispatch, participant, conference, participantId, t]);
    const handleVolumeControl = useCallback((e) => {
        var _a, _b;
        (_b = (_a = e === null || e === void 0 ? void 0 : e.event) === null || _a === void 0 ? void 0 : _a.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(_a);
        dispatch(openDialog(VolumeControlDialog, { participantID: participantId }));
    }, [dispatch, participantId]);
    const handleMuteAudio = useCallback((e) => {
        var _a, _b;
        (_b = (_a = e === null || e === void 0 ? void 0 : e.event) === null || _a === void 0 ? void 0 : _a.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(_a);
        if (isAudioMuted) {
            dispatch(muteRemoteParticipant(participantId, MEDIA_TYPE.AUDIO));
        }
        else {
            dispatch(openDialog(MuteRemoteParticipantDialog, { participantID: participantId }));
        }
    }, [dispatch, participantId, isAudioMuted]);
    const handleMuteVideo = useCallback((e) => {
        var _a, _b;
        (_b = (_a = e === null || e === void 0 ? void 0 : e.event) === null || _a === void 0 ? void 0 : _a.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(_a);
        if (isVideoMuted) {
            dispatch(muteRemoteParticipant(participantId, MEDIA_TYPE.VIDEO));
        }
        else {
            dispatch(openDialog(MuteRemoteParticipantsVideoDialog, { participantID: participantId }));
        }
    }, [dispatch, participantId, isVideoMuted]);
    const handleGrantModerator = useCallback((e) => {
        var _a, _b;
        (_b = (_a = e === null || e === void 0 ? void 0 : e.event) === null || _a === void 0 ? void 0 : _a.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(_a);
        dispatch(openGrantModeratorDialog(participantId));
    }, [dispatch, participantId]);
    const handleMuteEveryoneElse = useCallback((e) => {
        var _a, _b;
        (_b = (_a = e === null || e === void 0 ? void 0 : e.event) === null || _a === void 0 ? void 0 : _a.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(_a);
        dispatch(openDialog(MuteEveryoneDialog, { exclude: [participantId] }));
    }, [dispatch, participantId]);
    const handleMuteEveryoneElseVideo = useCallback((e) => {
        var _a, _b;
        (_b = (_a = e === null || e === void 0 ? void 0 : e.event) === null || _a === void 0 ? void 0 : _a.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(_a);
        dispatch(openDialog(MuteEveryonesVideoDialog, { exclude: [participantId] }));
    }, [dispatch, participantId]);
    const handleKick = useCallback((e) => {
        var _a, _b;
        (_b = (_a = e === null || e === void 0 ? void 0 : e.event) === null || _a === void 0 ? void 0 : _a.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(_a);
        dispatch(openDialog(KickRemoteParticipantDialog, { participantID: participantId }));
    }, [dispatch, participantId]);
    const handleForceMuteVideo = useCallback((e) => {
        var _a, _b;
        (_b = (_a = e === null || e === void 0 ? void 0 : e.event) === null || _a === void 0 ? void 0 : _a.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(_a);
        dispatch(openDialog(ForceMuteRemoteParticipantVideoDialog, { participantID: participantId }));
    }, [dispatch, participantId]);
    const handleForceMuteAudio = useCallback((e) => {
        var _a, _b;
        (_b = (_a = e === null || e === void 0 ? void 0 : e.event) === null || _a === void 0 ? void 0 : _a.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(_a);
        dispatch(openDialog(ForceMuteRemoteDialog, { participantID: participantId }));
    }, [dispatch, participantId]);
    const handleDemoteToVisitor = useCallback((e) => {
        var _a, _b;
        (_b = (_a = e === null || e === void 0 ? void 0 : e.event) === null || _a === void 0 ? void 0 : _a.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(_a);
        dispatch(openDialog(DemoteToVisitorDialog, { participantID: participantId }));
    }, [dispatch, participantId]);
    const handleClearRaisedHand = useCallback((e) => {
        var _a, _b;
        (_b = (_a = e === null || e === void 0 ? void 0 : e.event) === null || _a === void 0 ? void 0 : _a.stopPropagation) === null || _b === void 0 ? void 0 : _b.call(_a);
        if (participant) {
            clearRaisedHandByParticipant(participant);
        }
    }, [participant]);
    // Early return AFTER all hooks have been called
    if (!participant) {
        return null;
    }
    return (React.createElement(Menu, { id: menuId },
        showParticipantName && (React.createElement(Item, { disabled: true },
            React.createElement("span", { style: { fontWeight: 'bold', fontSize: '14px', color: '#fff', pointerEvents: 'none' } }, sliceLengthyCharacters({
                char: (_b = participant === null || participant === void 0 ? void 0 : participant.name) !== null && _b !== void 0 ? _b : '',
                shouldBeDotted: true,
                charLength: 20
            }) || String(t('guestUser'))))),
        !isFakeParticipant && !isPrivateChatDisabled && (React.createElement(Item, { onClick: handlePrivateMessage },
            React.createElement("span", { className: 'popupmenu__icon' },
                React.createElement(Icon, { src: IconMessage })),
            String(t('toolbar.privateMessage')))),
        !isFakeParticipant && !isScreenShare && (React.createElement(Item, { onClick: handleObjectFit },
            React.createElement("span", { className: 'popupmenu__icon' },
                React.createElement(Icon, { src: objectFit === 'cover' ? IconContain : IconCover })),
            React.createElement("span", null, objectFit === 'cover' ? String(t('tooltip.switchToContain')) : String(t('tooltip.switchToCover'))))),
        !isFakeParticipant && !isScreenShare && (React.createElement(Item, { onClick: handleToggleZoomShare },
            React.createElement("span", { className: 'popupmenu__icon' },
                React.createElement(Icon, { src: isZoomForEveryone ? IconZoomOut : IconZoomIn })),
            React.createElement("span", null, isZoomForEveryone ? String(t('dialog.zoomForMe', 'Zoom for Me')) : String(t('dialog.zoomShareConfirm', 'Zoom for Everyone'))))),
        _isModerator && (!(participant === null || participant === void 0 ? void 0 : participant.webinarHidden) || (participant === null || participant === void 0 ? void 0 : participant.moderatorPin)) && (React.createElement(Item, { onClick: participantModeratorPin },
            React.createElement("span", { className: 'popupmenu__icon' },
                React.createElement(Icon, { src: IconModeratorPin })),
            React.createElement("span", null, (participant === null || participant === void 0 ? void 0 : participant.moderatorPin) ? String(t('videothumbnail.unpinEveryone')) : String(t('videothumbnail.pinEveryone'))))),
        React.createElement(Item, { onClick: handlePin },
            React.createElement("span", { className: 'popupmenu__icon' },
                React.createElement(Icon, { src: IconUserPinned })),
            String(t(currentlyPinned ? 'videothumbnail.unpinMe' : 'videothumbnail.pinMe'))),
        (window.innerWidth < 500 || isDeviceLandScape()) && (React.createElement(Item, { onClick: handleLargeVideoFullscreen },
            React.createElement("span", { className: 'popupmenu__icon' },
                React.createElement(Icon, { src: IconUserPinned })),
            String(t(largevideofullscreen ? 'videothumbnail.exitLargeVideoFullscreen' : 'videothumbnail.makeLargeVideoFullscreen')))),
        !isFakeParticipant && (React.createElement(Item, { onClick: handleVolumeControl },
            React.createElement("span", { className: 'popupmenu__icon' },
                React.createElement(Icon, { src: IconVolume })),
            String(t('volumeSlider.label')))),
        _isModerator && !isFakeParticipant && (React.createElement(React.Fragment, null,
            !disableRemoteMute && !isAudioMuted && (React.createElement(Item, { onClick: handleMuteAudio },
                React.createElement("span", { className: 'popupmenu__icon' },
                    React.createElement(Icon, { src: IconMicDisabled })),
                String(t(isAudioMuted ? 'videothumbnail.unmute' : 'videothumbnail.domute')))),
            !disableRemoteMute && !isVideoMuted && (React.createElement(Item, { onClick: handleMuteVideo },
                React.createElement("span", { className: 'popupmenu__icon' },
                    React.createElement(Icon, { src: IconCameraDisabled })),
                String(t(isVideoMuted ? 'videothumbnail.unmuteVideo' : 'participantsPane.actions.stopVideo')))),
            React.createElement(Item, { onClick: handleForceMuteVideo },
                React.createElement("span", { className: 'popupmenu__icon' },
                    React.createElement(Icon, { src: IconCameraDisabled })),
                React.createElement("span", null, (lockVideo === null || lockVideo === void 0 ? void 0 : lockVideo[participantId]) !== undefined && lockVideo[participantId] === true
                    ? String(t('participantsPane.actions.unForceMuteVideo'))
                    : String(t('participantsPane.actions.forceMuteVideo')))),
            React.createElement(Item, { onClick: handleForceMuteAudio },
                React.createElement("span", { className: 'popupmenu__icon' },
                    React.createElement(Icon, { src: IconMicDisabled })),
                React.createElement("span", null, (lockAudio === null || lockAudio === void 0 ? void 0 : lockAudio[participantId]) !== undefined && lockAudio[participantId] === true
                    ? String(t('participantsPane.actions.unForceMute'))
                    : String(t('participantsPane.actions.forceMute')))),
            isMainModerator &&
                React.createElement(Item, { onClick: handleGrantModerator },
                    React.createElement("span", { className: 'popupmenu__icon' },
                        React.createElement(Icon, { src: IconCrown })),
                    String(t(isTargetModerator ? 'dialog.revokeModerationTitle' : 'videothumbnail.grantModerator'))),
            React.createElement(Item, { onClick: handleMuteEveryoneElse },
                React.createElement("span", { className: 'popupmenu__icon' },
                    React.createElement(Icon, { src: IconMuteEveryoneElse })),
                String(t('videothumbnail.domuteOthers'))),
            React.createElement(Item, { onClick: handleMuteEveryoneElseVideo },
                React.createElement("span", { className: 'popupmenu__icon' },
                    React.createElement(Icon, { src: IconMuteVideoEveryoneElse })),
                String(t('videothumbnail.domuteVideoOfOthers'))),
            !disableKick && (React.createElement(Item, { onClick: handleKick },
                React.createElement("span", { className: 'popupmenu__icon' },
                    React.createElement(Icon, { src: IconKick })),
                String(t('videothumbnail.kick')))),
            isVisitorsModeSupported_flag && (React.createElement(Item, { onClick: handleDemoteToVisitor },
                React.createElement("span", { className: 'popupmenu__icon' },
                    React.createElement(Icon, { src: IconUserGroup })),
                String(t('videothumbnail.demote')))),
            (participant === null || participant === void 0 ? void 0 : participant.raisedHand) && (React.createElement(Item, { onClick: handleClearRaisedHand },
                React.createElement("span", { className: 'popupmenu__icon' },
                    React.createElement(Icon, { src: IconRaisedHandSlash })),
                String(t('participantsPane.actions.clearRaisedHand'))))))));
}
/**
 * Helper function to show context menu for a specific participant
 * @param {MouseEvent} event - The mouse event that triggered the context menu
 * @param {string} videoSpanId - The video container span ID
 */
export function showRemoteVideoContextMenu(event, videoSpanId) {
    var _a;
    event.preventDefault();
    (_a = event.stopPropagation) === null || _a === void 0 ? void 0 : _a.call(event);
    // Import contextMenu from react-contexify dynamically
    import('react-contexify').then(({ contextMenu }) => {
        contextMenu.show({
            id: `context-menu-${videoSpanId}`,
            event: event
        });
    });
}
